import { useEffect, useState } from "react";

export interface MetabaseLocationState {
  /** Non-empty filter values by param name (tab is kept separate). */
  params: Record<string, string>;
  /** Active dashboard tab slug, like "4-orders-list". */
  tab: string | null;
  /** The raw search string Metabase sent, like "?customer=...&tab=...". */
  search: string;
}

interface MetabaseLocationMessage {
  metabase?: {
    type?: string;
    location?: {
      search?: string;
    };
  };
}

const parseSearch = (search: string): MetabaseLocationState => {
  const sp = new URLSearchParams(search);
  const params: Record<string, string> = {};
  let tab: string | null = null;

  sp.forEach((value, key) => {
    if (key === "tab") {
      tab = value || null;
      return;
    }
    // Metabase sends every param name, most with no value. Keep only the set ones.
    if (value) {
      params[key] = value;
    }
  });

  return { params, tab, search };
};

/**
 * Reads the filter state of an embedded Metabase dashboard.
 *
 * Metabase sends a postMessage with type "location" whenever a filter or tab
 * changes, and it includes the full search string. Metabase still owns the
 * filter UI. This hook only reads what it sends, so we can save and reload
 * reports without building our own filter controls.
 *
 * Pass metabaseOrigin to check the message source. When left out, messages are
 * matched by their payload shape only.
 */
export function useMetabaseLocation(
  metabaseOrigin?: string,
): MetabaseLocationState | null {
  const [state, setState] = useState<MetabaseLocationState | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (metabaseOrigin && event.origin !== metabaseOrigin) return;

      const data = event.data as MetabaseLocationMessage | undefined;
      if (data?.metabase?.type !== "location") return;

      setState(parseSearch(data.metabase.location?.search ?? ""));
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [metabaseOrigin]);

  return state;
}
